import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Task } from '../../../../types/task';
import { TaskItemV2 } from './TaskItemV2';
import { useAtomValue } from 'jotai';
import { isTaskItemDraggingAtom } from '../../../../atoms/v3/taskAtoms';

interface DraggableTaskItemV2Props {
  task: Task;
  onToggle: (taskId: string, completed: boolean) => void;
  onClick: (taskId: string) => void;
  onTaskUpdate?: (taskId: string, updates: { projectId?: string; vital?: boolean; urgent?: boolean }) => void;
  compact?: boolean;
  viewMode?: 'list' | 'timeline';
  showBackground?: boolean; // 是否显示背景色（默认true）
  dropZoneType?: 'project' | 'importance' | 'both';
}

/**
 * 可拖拽的TaskItemV2组件
 * 包装TaskItemV2组件，使其可拖拽
 * 支持项目和重要性属性的拖拽修改
 */
export const DraggableTaskItemV2: React.FC<DraggableTaskItemV2Props> = ({
  task,
  onToggle,
  onClick,
  onTaskUpdate,
  compact = false,
  viewMode = 'list',
  showBackground = true,
  dropZoneType = 'both'
}) => {
  const isTaskItemDragging = useAtomValue(isTaskItemDraggingAtom);

  // 检测是否为触摸设备
  const isTouchDevice = 'ontouchstart' in window ||
                        navigator.maxTouchPoints > 0 ||
                        window.matchMedia('(pointer: coarse)').matches;

  // 使用dnd-kit的useDraggable钩子
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `task-${task.id}`,
    data: {
      type: 'task-item',
      taskId: task.id,
      task: task,
      dropZoneType: dropZoneType
    }
  });

  // 计算拖拽样式
  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 9999 : 'auto',
    cursor: isDragging ? 'grabbing' : (isTouchDevice ? 'default' : 'pointer'),
    opacity: isTaskItemDragging && !isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`touch-manipulation ${isTouchDevice ? 'select-none' : ''}`}
      title={isTouchDevice ? '长按拖拽移动任务' : '拖拽移动任务'}
    >
      <TaskItemV2
        task={task}
        onToggle={onToggle}
        onClick={onClick}
        compact={compact}
        viewMode={viewMode}
        showBackground={showBackground}
      />
    </div>
  );
};

// 使用 React.memo 包装组件，避免不必要的重新渲染
export default React.memo(DraggableTaskItemV2);
